import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import {
    publicReportApi,
    publicReportProblemOf,
    type PublicLanguage,
} from '@/entities/publicReport';

import { useLinkRefresh, type UsePublicReportResult } from './usePublicReport';

export const publicReportPreviewKey = (reportUuid: string) =>
    ['public-report', 'preview', reportUuid] as const;

/** In de preview gaat er niets naar buiten; de formulieren zeggen dat zelf. */
async function notInPreview(): Promise<void> {
    throw new Error('In de preview wordt niets verstuurd.');
}

/**
 * De klantpagina zoals kantoor hem bekijkt: `GET /reports/{report}/preview`.
 *
 * Dezelfde vorm als `usePublicReport`, zodat koper en kantoor precies dezelfde
 * pagina zien - maar met de sessie in plaats van het token. Dat telt geen bezoek
 * en vraagt geen toegangscode. Een verlopen of ingetrokken link geeft dezelfde
 * foutpagina als bij de koper, in de huisstijl uit het foutantwoord.
 *
 * Doorsturen en een vraag stellen versturen hier niets. De sleutel begint met
 * `public`, dus ook deze pagina komt niet in de bewaarde querycache.
 */
export function usePublicReportPreview(reportUuid: string | null): UsePublicReportResult {
    const [requested, setRequested] = useState<PublicLanguage | null>(null);

    const query = useQuery({
        queryKey: publicReportPreviewKey(reportUuid ?? ''),
        queryFn: () => publicReportApi.preview(reportUuid ?? ''),
        enabled: reportUuid !== null && reportUuid !== '',
        retry: false,
        gcTime: 0,
        staleTime: 0,
        refetchOnReconnect: false,
    });

    const problem = useMemo(
        () => (query.error ? publicReportProblemOf(query.error) : null),
        [query.error],
    );

    const { refetch } = query;
    const refreshLinks = useLinkRefresh(refetch);

    return {
        report: query.data,
        tenant: query.data?.tenant ?? problem?.tenant ?? null,
        /* De inhoud is Nederlands; een andere taal verandert alleen de omlijsting. */
        language: requested ?? query.data?.language ?? 'nl',
        setLanguage: setRequested,
        problem,
        loading: query.isPending,
        checkingCode: false,
        submitAccessCode: () => undefined,
        retry: () => void refetch(),
        share: notInPreview,
        ask: notInPreview,
        refreshLinks,
    };
}
